import SafeUncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { useCampaignReports } from '../CampaignReportsContext';
import { CampaignReportStatus } from './EngagementSurvey';
import EngagementSurveyReportNotificationModalEditor from './EngagementSurveyReportNotificationModalEditor';

interface EngagementSurveyReportsTableNotificationButtonProps {
  reportId: number;
  reportStatus?: CampaignReportStatus;
}

const EngagementSurveyReportsTableNotificationButton: React.FC<
  EngagementSurveyReportsTableNotificationButtonProps
> = ({ reportId, reportStatus }) => {
  const { refreshEngagementSurveyListVersion } = useCampaignReports();
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  // Check if the button should be disabled (when status is UNPUBLISHED)
  const isDisabled = reportStatus === CampaignReportStatus.UNPUBLISHED;

  // Create a unique ID for the wrapper element
  const wrapperId = `notification-button-wrapper-${reportId}`;

  const handleOnClick = useCallback(() => {
    setIsOpen(true);
    // Reset the flag when opening the modal
    //hasCalledRefreshRef.current = false;
  }, []);

  const handleOnClosed = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOnCallback = useCallback(() => {
    refreshEngagementSurveyListVersion();
    handleOnClosed();
  }, [handleOnClosed, refreshEngagementSurveyListVersion]);

  // Get the appropriate tooltip message based on button state
  const tooltipMessage = isDisabled
    ? formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_reports_table_notification_button.tooltip.disabled',
        defaultMessage:
          'Notifications are not available for unpublished reports.',
      })
    : formatMessage({
        id: 'app.views.administration.campaign_reports.engagement_survey.engagement_survey_reports_table_notification_button.tooltip.enabled',
        defaultMessage: 'Send notifications',
      });

  return (
    <>
      {/* Wrap the button in a div for tooltip targeting */}
      <div
        id={wrapperId}
        data-testid={wrapperId}
        style={isDisabled ? { cursor: 'not-allowed' } : {}}
      >
        <Button
          className="ms-3 btn btn-light btn-sm"
          onClick={handleOnClick}
          disabled={isDisabled}
          style={isDisabled ? { pointerEvents: 'none' } : {}}
        >
          <i className="fe fe-bell" />
        </Button>
      </div>
      {/* Add the tooltip using SafeUncontrolledPopover targeting the wrapper */}
      <SafeUncontrolledPopover
        placement="top"
        trigger="hover"
        target={wrapperId}
      >
        {tooltipMessage}
      </SafeUncontrolledPopover>
      <EngagementSurveyReportNotificationModalEditor
        reportId={reportId}
        isOpen={isOpen}
        onClosed={handleOnClosed}
        onCallback={handleOnCallback}
      />
    </>
  );
};

export default React.memo(EngagementSurveyReportsTableNotificationButton);
